import React, { useEffect, useState } from "react";
import { Plus, Edit2, Trash2, Eye, EyeOff, GripVertical, X, Croissant, Coffee, CalendarDays, Flame, Cookie, CupSoda, UtensilsCrossed, Star, Upload } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";

const ICONS = { Croissant, Coffee, CalendarDays, Flame, Cookie, CupSoda, UtensilsCrossed };
const DIET = ["vegetarisch", "vegan"];
const COMMON_ALLERGENS = ["Gluten", "Milch", "Eier", "Soja", "Nüsse", "Erdnüsse", "Fisch", "Krebstiere", "Sellerie", "Senf", "Sesam", "Sulfite"];
const EMPTY = { category: "", name: "", price: 0, ma_price: "", allergens: [], allergen_codes: [], allergens_status: "unconfirmed", description: "", image: "", dietary: [], active: true };

export function MenuManager() {
  const [cats, setCats] = useState([]);
  const [active, setActive] = useState("");
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [suggesting, setSuggesting] = useState(false);
  const [allergenNote, setAllergenNote] = useState("");
  const codeLabel = (c) => catalog.find((a) => a.code === c)?.label || c;
  const setCodes = (codes) => setEdit((e) => ({ ...e, allergen_codes: codes, allergens_status: "unconfirmed" }));
  const suggestAllergens = async () => {
    if (!edit.name.trim()) { toast.error("Bitte zuerst den Namen eingeben."); return; }
    setSuggesting(true); setAllergenNote("");
    try {
      const { data } = await api.post("/admin/allergens/suggest", { name: edit.name });
      setEdit((e) => ({ ...e, allergen_codes: data.codes || [], allergens_status: "unconfirmed" }));
      setAllergenNote(data.note || "");
    } catch (e) { toast.error("Vorschlag fehlgeschlagen"); }
    setSuggesting(false);
  };

  const loadCats = () => api.get("/admin/menu/categories").then((r) => { setCats(r.data); if (!active && r.data.length) setActive(r.data[0].id); });
  const loadItems = (cat) => api.get(`/admin/menu/items?category=${cat}`).then((r) => setItems(r.data));
  useEffect(() => { loadCats(); api.get("/allergens/catalog").then((r) => setCatalog(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (active) loadItems(active); }, [active]);

  const activeCat = cats.find((c) => c.id === active);
  const isWeekly = activeCat?.type === "weekly";

  const save = async () => {
    if (!edit.name.trim() || !(edit.price > 0)) { toast.error("Name und Preis (> 0) sind Pflicht."); return; }
    if (edit.active && edit.allergens_status !== "confirmed") { toast.error("Vor Veröffentlichung bitte Allergene prüfen und bestätigen (EU-VO 1169/2011). Als Entwurf ausblenden ist ohne Bestätigung möglich."); return; }
    try {
      const body = { ...edit, category: active, ma_price: (edit.ma_price === "" || edit.ma_price === null || edit.ma_price === undefined) ? null : parseFloat(edit.ma_price) };
      if (edit.id) await api.put(`/admin/menu/items/${edit.id}`, body);
      else await api.post("/admin/menu/items", body);
      toast.success("Gespeichert"); setEdit(null); loadItems(active);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const toggle = async (it) => { await api.put(`/admin/menu/items/${it.id}`, { ...it, active: !it.active }); loadItems(active); };
  const del = async (id) => { if (!window.confirm("Artikel löschen?")) return; await api.delete(`/admin/menu/items/${id}`); toast.success("Gelöscht"); loadItems(active); };

  const onDrop = async (toIdx) => {
    if (dragIdx === null || dragIdx === toIdx) return;
    const arr = [...items];
    const [moved] = arr.splice(dragIdx, 1);
    arr.splice(toIdx, 0, moved);
    setItems(arr); setDragIdx(null);
    await api.post("/admin/menu/items/reorder", { category: active, ids: arr.map((i) => i.id) });
  };

  const uploadImg = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEdit((e) => ({ ...e, image: data.url })); toast.success("Bild hochgeladen");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const inp = "w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-[#D62B3A] outline-none text-sm";
  return (
    <div>
      {/* Category chips */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 mb-5">
        {cats.map((c) => {
          const Icon = ICONS[c.icon] || UtensilsCrossed;
          return (
            <button key={c.id} onClick={() => setActive(c.id)} data-testid={`admin-menu-cat-${c.id}`}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap ${active === c.id ? "bg-[#D62B3A] text-white" : "bg-white border border-gray-200 text-gray-700"}`}>
              <Icon className="w-4 h-4" /> {c.name}
            </button>
          );
        })}
      </div>

      {isWeekly ? (
        <div className="bg-white rounded-xl border border-gray-100 p-6" data-testid="menu-weekly-note">
          <div className="flex items-center gap-2 font-semibold"><CalendarDays className="w-5 h-5 text-[#D62B3A]" /> Wochenmenü (KW-basiert)</div>
          <p className="mt-2 text-sm text-gray-600">Das Wochenmenü wechselt wöchentlich und wird im Reiter <strong>„Menüs"</strong> gepflegt (KW-basierter Workflow). Alte Wochen bleiben archiviert, auf der Kundenseite erscheint nur die aktuelle KW.</p>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-gray-500">{items.length} Artikel {activeCat?.subtitle ? `· ${activeCat.subtitle}` : ""}</p>
            <button onClick={() => setEdit({ ...EMPTY })} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#D62B3A] text-white font-semibold text-sm" data-testid="menu-item-new">
              <Plus className="w-4 h-4" /> Artikel hinzufügen
            </button>
          </div>
          <div className="space-y-2">
            {items.map((it, idx) => (
              <div key={it.id} draggable onDragStart={() => setDragIdx(idx)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(idx)}
                className={`bg-white rounded-xl border border-gray-100 p-3 flex items-center gap-3 ${!it.active ? "opacity-50" : ""}`} data-testid={`admin-menu-item-${it.id}`}>
                <GripVertical className="w-4 h-4 text-gray-300 cursor-grab shrink-0" />
                {it.image ? <img src={it.image} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" /> : <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center shrink-0"><UtensilsCrossed className="w-5 h-5 text-gray-300" /></div>}
                <div className="min-w-0 flex-1">
                  <div className="font-semibold truncate">{it.name}</div>
                  <div className="text-xs text-gray-500">{it.price?.toFixed(2)} €{it.ma_price != null ? ` · MA ${it.ma_price.toFixed(2)} €` : ""} · <span className="text-[#C81E33]"><Star className="w-3 h-3 inline" /> {it.points_required} Pkt.</span> {it.allergens?.length ? `· ${it.allergens.join(", ")}` : ""}</div>
                </div>
                <button onClick={() => toggle(it)} title={it.active ? "Ausblenden" : "Einblenden"} className="p-2 rounded-lg hover:bg-gray-100" data-testid={`menu-item-toggle-${it.id}`}>{it.active ? <Eye className="w-4 h-4 text-green-600" /> : <EyeOff className="w-4 h-4 text-gray-400" />}</button>
                <button onClick={() => setEdit(it)} className="p-2 rounded-lg hover:bg-gray-100" data-testid={`menu-item-edit-${it.id}`}><Edit2 className="w-4 h-4" /></button>
                <button onClick={() => del(it.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500" data-testid={`menu-item-del-${it.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            {!items.length && <p className="text-gray-400 py-6 text-center">Noch keine Artikel. Füge den ersten hinzu.</p>}
          </div>
        </>
      )}

      {edit && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center overflow-y-auto p-4" onClick={() => setEdit(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg my-8 p-6" onClick={(e) => e.stopPropagation()} data-testid="menu-item-editor">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold">{edit.id ? "Artikel bearbeiten" : "Neuer Artikel"} · {activeCat?.name}</h3>
              <button onClick={() => setEdit(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <input placeholder="Name *" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className={inp} data-testid="mi-name" />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500">Preis (€) *</label>
                  <input type="number" step="0.10" value={edit.price} onChange={(e) => setEdit({ ...edit, price: parseFloat(e.target.value || 0) })} className={inp} data-testid="mi-price" />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Punkte (automatisch berechnet)</label>
                  <div className="px-3 py-2 rounded-lg bg-[#FDF2F4] text-[#C81E33] text-sm font-semibold" data-testid="mi-points-preview">{Math.round((edit.price * 40) / 10) * 10} Punkte</div>
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-500">Mitarbeiterpreis (MA) · € · optional</label>
                <input type="number" step="0.10" placeholder="z. B. 2,50 (leer = kein MA-Preis)" value={edit.ma_price ?? ""} onChange={(e) => setEdit({ ...edit, ma_price: e.target.value })} className={inp} data-testid="mi-ma-price" />
                <p className="mt-1 text-xs text-gray-400">Separater Preis für Mitarbeitende. Gilt nur für die Speisekarte, nicht für Veranstaltungs-Catering.</p>
              </div>
              <div className="-mt-1 text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
                Preis: <strong>{(edit.price || 0).toFixed(2)} €</strong> → <strong className="text-[#C81E33]">{Math.round((edit.price * 40) / 10) * 10} Punkte</strong>
                <span className="text-gray-400"> · Formel: Preis × 40, kaufmännisch auf 10 gerundet (nicht editierbar)</span>
              </div>
              <textarea placeholder="Beschreibung" rows={2} value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} className={inp} data-testid="mi-desc" />
              {/* Automatische Allergenerkennung */}
              <div className="rounded-xl border border-gray-200 p-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">Allergene <span className="text-gray-400 font-normal">· EU-VO 1169/2011</span></label>
                  <button type="button" onClick={suggestAllergens} disabled={suggesting} className="text-xs font-semibold text-[#D62B3A] disabled:opacity-50" data-testid="mi-allergen-suggest">{suggesting ? "Analysiere…" : "Automatisch vorschlagen"}</button>
                </div>
                <div className={`mt-2 flex flex-wrap gap-1.5 rounded-lg p-2 ${edit.allergens_status === "confirmed" ? "border border-[#6F6F6F]/40" : "border border-dashed border-amber-400 bg-amber-50/40"}`} data-testid="mi-allergen-chips">
                  {(edit.allergen_codes || []).map((c) => (
                    <span key={c} className="inline-flex items-center gap-1 bg-[#6F6F6F] text-white text-xs px-2 py-1 rounded-full">#{c}-{codeLabel(c)}<button type="button" onClick={() => setCodes((edit.allergen_codes || []).filter((x) => x !== c))} data-testid={`mi-allergen-remove-${c}`}><X className="w-3 h-3" /></button></span>
                  ))}
                  {!(edit.allergen_codes || []).length && <span className="text-xs text-gray-400">Keine Allergene ausgewählt</span>}
                </div>
                {allergenNote && <p className="mt-1 text-xs text-amber-700" data-testid="mi-allergen-note">{allergenNote}</p>}
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <select value="" onChange={(e) => { if (e.target.value) setCodes([...(edit.allergen_codes || []), e.target.value]); }} className="text-sm rounded-lg border border-gray-200 px-2 py-1.5" data-testid="mi-allergen-add">
                    <option value="">+ Allergen hinzufügen</option>
                    {catalog.filter((a) => !(edit.allergen_codes || []).includes(a.code)).map((a) => <option key={a.code} value={a.code}>{a.code} · {a.label}</option>)}
                  </select>
                  {edit.allergens_status === "confirmed"
                    ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700" data-testid="mi-allergen-status">✓ Geprüft & bestätigt</span>
                    : <button type="button" onClick={() => setEdit({ ...edit, allergens_status: "confirmed" })} className="text-xs font-semibold bg-[#6F6F6F] text-white px-3 py-1.5 rounded-lg" data-testid="mi-allergen-confirm">Bestätigen</button>}
                </div>
                <p className="mt-2 text-[11px] text-gray-400">Automatische Vorschläge dienen der Arbeitserleichterung und ersetzen NICHT die fachliche Prüfung der tatsächlichen Rezeptur. Bitte immer die reale Zubereitung prüfen.</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Allergene * <span className="text-gray-400 font-normal">(EU-VO 1169/2011)</span></label>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {COMMON_ALLERGENS.map((a) => {
                    const on = edit.allergens.includes(a);
                    const noneOn = edit.allergens.includes("Keine");
                    return <button key={a} type="button" disabled={noneOn} onClick={() => setEdit({ ...edit, allergens: on ? edit.allergens.filter((x) => x !== a) : [...edit.allergens.filter((x) => x !== "Keine"), a] })}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium ${on ? "bg-amber-100 text-amber-800 border border-amber-300" : "bg-gray-50 text-gray-600 border border-gray-200"} ${noneOn ? "opacity-40 cursor-not-allowed" : ""}`}>{a}</button>;
                  })}
                  <button type="button" onClick={() => setEdit({ ...edit, allergens: edit.allergens.includes("Keine") ? [] : ["Keine"] })}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium ${edit.allergens.includes("Keine") ? "bg-green-100 text-green-700 border border-green-300" : "bg-gray-50 text-gray-600 border border-gray-200"}`}>Keine</button>
                </div>
              </div>
              <div className="flex gap-4">
                {DIET.map((d) => (
                  <label key={d} className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={edit.dietary.includes(d)} onChange={() => setEdit({ ...edit, dietary: edit.dietary.includes(d) ? edit.dietary.filter((x) => x !== d) : [...edit.dietary, d] })} className="accent-[#D62B3A]" />
                    {d.charAt(0).toUpperCase() + d.slice(1)}
                  </label>
                ))}
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Bild (optional)</label>
                <div className="mt-1 flex items-center gap-3">
                  {edit.image && <img src={edit.image} alt="" className="w-16 h-16 rounded-lg object-cover" />}
                  <label className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 cursor-pointer text-sm hover:bg-gray-50">
                    <Upload className="w-4 h-4" /> Bild wählen
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadImg(e.target.files[0])} data-testid="mi-image" />
                  </label>
                  {edit.image && <button type="button" onClick={() => setEdit({ ...edit, image: "" })} className="text-sm text-gray-400 hover:text-[#D62B3A]">Entfernen</button>}
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={edit.active} onChange={(e) => setEdit({ ...edit, active: e.target.checked })} className="accent-[#D62B3A]" data-testid="mi-active" /> Sichtbar (verfügbar)
              </label>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={save} className="flex-1 py-3 rounded-xl bg-[#D62B3A] text-white font-semibold" data-testid="mi-save">Speichern</button>
              <button onClick={() => setEdit(null)} className="px-6 py-3 rounded-xl border border-gray-200">Abbrechen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
